<template>
  <van-pull-refresh v-model="refreshing" class="page-pull-refresh" @refresh="onRefresh">
    <div class="access-records-page">
      <section class="filter-card">
        <van-search
          v-model.trim="filters.keyword"
          placeholder="输入姓名、身份证号或车牌号搜索"
          show-action
          @search="onSearch"
          @clear="onClearKeyword"
        >
          <template #action>
            <div @click="onSearch">搜索</div>
          </template>
        </van-search>

        <div class="module-filter-row">
          <van-field
            readonly
            clickable
            is-link
            label="登记类型"
            :value="selectedModuleTitle"
            placeholder="全部类型"
            @click="showModulePicker = true"
          />
        </div>

        <div class="filter-actions">
          <van-button round block type="default" @click="resetFilters">重置</van-button>
          <van-button round block color="#1556c7" @click="fetchRecords({ reset: true })">查询</van-button>
        </div>
      </section>

      <section class="summary-card">
        <div class="summary-item">
          <span>登记记录</span>
          <strong>{{ totalCount }}</strong>
        </div>
        <div class="summary-item">
          <span>当前筛选</span>
          <strong>{{ records.length }}</strong>
        </div>
      </section>

      <section class="record-list-section">
        <van-loading v-if="loading" type="spinner" color="#1556c7" class="loading-center">加载中...</van-loading>
        <van-list
          v-else
          v-model="loadingMore"
          :finished="finished"
          :immediate-check="true"
          :offset="120"
          finished-text="没有更多了"
          @load="onLoadMore"
        >
          <div class="record-list">
            <van-card
              v-for="record in records"
              :key="record.id || record.recordNo"
              class="record-card"
              @click="openDetail(record)"
            >
              <template #title>
                <div class="record-header">
                  <div>
                    <div class="record-title">{{ record.name || record.driverName || record.plateNumber || '-' }}</div>
                    <div class="record-subtitle">{{ moduleTitle(record.moduleType) }}</div>
                  </div>
                  <span class="record-time">{{ formatTime(record.submitTime || record.createTime || record.passOrRegisterTime) }}</span>
                </div>
              </template>
              <template #desc>
                <div class="record-meta-grid">
                  <div class="record-meta-item"><span>身份证号</span><strong>{{ record.idCardNo || '-' }}</strong></div>
                  <div class="record-meta-item"><span>手机号</span><strong>{{ record.phoneNumber || record.wechatPhone || '-' }}</strong></div>
                  <div class="record-meta-item"><span>车牌号</span><strong>{{ record.plateNumber || '-' }}</strong></div>
                  <div class="record-meta-item"><span>船舶名称</span><strong>{{ record.shipName || '-' }}</strong></div>
                </div>
              </template>
              <template #footer>
                <div class="record-actions" @click.stop>
                  <van-button size="small" round plain type="info" @click="openDetail(record)">详情</van-button>
                  <van-button size="small" round plain type="primary" @click="openEdit(record)">修改</van-button>
                  <van-button size="small" round plain type="warning" @click="openRemark(record)">备注</van-button>
                  <van-button size="small" round plain type="danger" @click="confirmVoid(record)">删除</van-button>
                </div>
              </template>
            </van-card>
          </div>
        </van-list>
        <van-empty v-if="!loading && records.length === 0" image="search" description="暂无登记记录" />
      </section>

      <van-popup v-model="showModulePicker" position="bottom" round :style="{ maxHeight: '70%' }">
        <div class="picker-popup">
          <div class="popup-header">
            <h3>选择登记类型</h3>
            <van-icon name="cross" @click="showModulePicker = false" />
          </div>
          <div class="picker-option" @click="selectModule('')">
            <span>全部类型</span>
            <van-icon v-if="!filters.moduleType" name="success" color="#16a56f" />
          </div>
          <div
            v-for="item in moduleOptions"
            :key="item.moduleType"
            class="picker-option"
            @click="selectModule(item.moduleType)"
          >
            <span>{{ item.title }}</span>
            <van-icon v-if="filters.moduleType === item.moduleType" name="success" color="#16a56f" />
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showDetailPopup" position="bottom" round :style="{ height: '76%' }">
        <div class="detail-popup">
          <div class="popup-header">
            <h3>登记详情</h3>
            <van-icon name="cross" @click="showDetailPopup = false" />
          </div>
          <div v-if="detailRecord" class="detail-list">
            <div class="detail-row"><span>记录编号</span><strong>{{ detailRecord.recordNo || detailRecord.id || '-' }}</strong></div>
            <div class="detail-row"><span>登记类型</span><strong>{{ moduleTitle(detailRecord.moduleType) }}</strong></div>
            <div class="detail-row"><span>提交时间</span><strong>{{ formatTime(detailRecord.submitTime || detailRecord.createTime) }}</strong></div>
            <div
              v-for="field in detailFields"
              :key="field.key"
              class="detail-row"
            >
              <span>{{ field.label }}</span>
              <strong>{{ displayFieldValue(field, detailRecord) }}</strong>
            </div>
            <div class="detail-row detail-row--block">
              <span>管理员备注</span>
              <strong>{{ detailRecord.remark || detailRecord.adminRemark || '-' }}</strong>
            </div>
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showEditPopup" position="bottom" round :style="{ height: '82%' }">
        <div class="edit-popup">
          <div class="popup-header">
            <h3>修改登记信息</h3>
            <van-icon name="cross" @click="showEditPopup = false" />
          </div>
          <div class="edit-form">
            <template v-for="field in editFields">
              <div v-if="isEditableField(field)" :key="field.key" class="form-block">
                <div class="field-label">{{ field.label }}</div>
                <van-field
                  v-if="field.type === 'text' || field.type === 'idCard' || field.type === 'phone' || field.type === 'phoneInput' || field.type === 'plate' || field.type === 'shipName' || field.type === 'number'"
                  v-model.trim="editForm[field.key]"
                  clearable
                  :type="field.type === 'number' ? 'number' : 'text'"
                  :maxlength="field.maxlength || 100"
                  :placeholder="`请输入${field.label}`"
                />
                <van-field
                  v-else-if="field.type === 'textarea'"
                  v-model.trim="editForm[field.key]"
                  type="textarea"
                  rows="3"
                  autosize
                  show-word-limit
                  :maxlength="field.maxlength || 200"
                  :placeholder="`请输入${field.label}`"
                />
                <van-field
                  v-else-if="field.type === 'datetime'"
                  :value="formatTime(editForm[field.key])"
                  readonly
                  clickable
                  is-link
                  @click="openEditDateTime(field)"
                />
                <van-field
                  v-else-if="field.type === 'select'"
                  readonly
                  clickable
                  is-link
                  :value="getOptionText(field, editForm[field.key])"
                  :placeholder="`请选择${field.label}`"
                  @click="openEditSelect(field)"
                />
              </div>
            </template>
          </div>
          <div class="popup-actions">
            <van-button round block type="default" @click="showEditPopup = false">取消</van-button>
            <van-button round block color="#16a56f" :loading="savingEdit" @click="submitEdit">保存修改</van-button>
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showRemarkPopup" position="bottom" round :style="{ height: '42%' }">
        <div class="remark-popup">
          <div class="popup-header">
            <h3>登记备注</h3>
            <van-icon name="cross" @click="showRemarkPopup = false" />
          </div>
          <van-field
            v-model.trim="remarkForm.remark"
            type="textarea"
            rows="5"
            autosize
            maxlength="300"
            show-word-limit
            placeholder="请输入管理员备注"
          />
          <div class="popup-actions">
            <van-button round block type="default" @click="showRemarkPopup = false">取消</van-button>
            <van-button round block color="#16a56f" :loading="savingRemark" @click="submitRemark">保存备注</van-button>
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showEditSelectPopup" position="bottom" round :style="{ maxHeight: '70%' }">
        <div class="picker-popup">
          <div class="popup-header">
            <h3>{{ activeEditSelectField ? `选择${activeEditSelectField.label}` : '请选择' }}</h3>
            <van-icon name="cross" @click="showEditSelectPopup = false" />
          </div>
          <div
            v-for="item in activeEditSelectOptions"
            :key="item.code"
            class="picker-option"
            @click="selectEditOption(item.code)"
          >
            <span>{{ item.description }}</span>
            <van-icon v-if="activeEditSelectField && editForm[activeEditSelectField.key] === item.code" name="success" color="#16a56f" />
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showEditDateTimePopup" position="bottom" round>
        <div class="datetime-popup">
          <div class="popup-header">
            <h3>{{ activeEditDateTimeField ? `选择${activeEditDateTimeField.label}` : '选择时间' }}</h3>
            <van-icon name="cross" @click="showEditDateTimePopup = false" />
          </div>
          <van-datetime-picker
            v-model="editDateTimeValue"
            type="datetime"
            :show-toolbar="false"
            :formatter="formatDateTimeColumn"
          />
          <div class="popup-actions">
            <van-button round block type="default" @click="showEditDateTimePopup = false">取消</van-button>
            <van-button round block color="#16a56f" @click="confirmEditDateTime">确定</van-button>
          </div>
        </div>
      </van-popup>
    </div>
  </van-pull-refresh>
</template>

<script>
import axios from '@/utils/request';
import globalDic, { fetchMultiDic } from '@/utils/dic';
import {
  ACCESS_REGISTRATION_MODULES,
  getAccessRegistrationGroups,
  getAccessRegistrationConfig
} from '@/utils/accessRegistration';

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

export default {
  name: 'AccessRecords',
  data() {
    return {
      filters: {
        keyword: '',
        moduleType: ''
      },
      records: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 20,
      loading: false,
      refreshing: false,
      loadingMore: false,
      finished: false,
      moduleOptions: getAccessRegistrationGroups().flatMap(group => group.items),
      optionsMap: {},
      showModulePicker: false,
      showDetailPopup: false,
      showEditPopup: false,
      showRemarkPopup: false,
      showEditSelectPopup: false,
      showEditDateTimePopup: false,
      detailRecord: null,
      editingRecord: null,
      editForm: {},
      remarkForm: {
        id: '',
        remark: ''
      },
      activeEditSelectField: null,
      activeEditDateTimeField: null,
      editDateTimeValue: new Date(),
      savingEdit: false,
      savingRemark: false
    };
  },
  computed: {
    selectedModuleTitle() {
      return this.filters.moduleType ? this.moduleTitle(this.filters.moduleType) : '';
    },
    detailFields() {
      return this.fieldsForRecord(this.detailRecord).filter(field => field.type !== 'image');
    },
    editFields() {
      return this.fieldsForRecord(this.editingRecord);
    },
    activeEditSelectOptions() {
      return this.activeEditSelectField ? this.getOptions(this.activeEditSelectField) : [];
    }
  },
  methods: {
    async bootstrapDictionaries() {
      const dicNames = [...new Set(Object.values(ACCESS_REGISTRATION_MODULES).flatMap(item => item.dictionaries || []))];
      let dicResult = {};
      try {
        dicResult = await fetchMultiDic(dicNames);
      } catch (error) {
        dicResult = {};
      }

      const optionsMap = {};
      Object.values(ACCESS_REGISTRATION_MODULES).forEach(config => {
        (config.fields || []).forEach(field => {
          if (field.type !== 'select') {
            return;
          }

          const remote = field.dicName && dicResult[field.dicName] && dicResult[field.dicName].length
            ? dicResult[field.dicName]
            : field.dicName && globalDic[field.dicName] && globalDic[field.dicName].length
              ? globalDic[field.dicName]
              : [];
          optionsMap[`${config.moduleType}:${field.optionGroup}`] = remote.length ? remote : (field.defaultOptions || []);
        });
      });
      this.optionsMap = optionsMap;
    },
    buildQueryPayload() {
      const keyword = (this.filters.keyword || '').trim();
      return {
        Keyword: keyword,
        ModuleType: this.filters.moduleType || '',
        Name: '',
        IdCardNo: '',
        PlateNumber: '',
        PageIndex: this.pageIndex,
        PageSize: this.pageSize
      };
    },
    async fetchRecords({ reset = false } = {}) {
      if (reset) {
        this.pageIndex = 1;
        this.finished = false;
        this.records = [];
        this.loading = true;
      } else if (this.finished) {
        return;
      } else {
        this.loadingMore = true;
      }

      try {
        const response = await axios.post('/api/AccessRegistration/Query', this.buildQueryPayload());
        if (!response || !response.data || !response.data.success) {
          this.$toast.fail((response && response.data && response.data.message) || '查询失败');
          this.finished = true;
          return;
        }

        const result = response.data.data || {};
        const items = result.items || result.records || [];
        this.records = reset ? items : this.records.concat(items);
        this.totalCount = Number(result.totalCount || result.total || items.length || 0);
        this.finished = result.hasMore === false || items.length < this.pageSize;
        if (items.length) {
          this.pageIndex += 1;
        }
      } catch (error) {
        this.$toast.fail('查询失败，请检查网络');
        if (reset) {
          this.records = [];
          this.totalCount = 0;
        }
        this.finished = true;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    onLoadMore() {
      this.fetchRecords();
    },
    async onRefresh() {
      try {
        await this.fetchRecords({ reset: true });
      } finally {
        this.refreshing = false;
      }
    },
    onSearch() {
      this.fetchRecords({ reset: true });
    },
    onClearKeyword() {
      this.filters.keyword = '';
      this.fetchRecords({ reset: true });
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        moduleType: ''
      };
      this.fetchRecords({ reset: true });
    },
    selectModule(moduleType) {
      this.filters.moduleType = moduleType;
      this.showModulePicker = false;
      this.fetchRecords({ reset: true });
    },
    async openDetail(record) {
      this.detailRecord = record;
      this.showDetailPopup = true;
      await this.loadDetail(record);
    },
    async loadDetail(record) {
      if (!record || !record.id) {
        return;
      }
      try {
        const response = await axios.post('/api/AccessRegistration/Detail', { Id: record.id });
        if (response && response.data && response.data.success && response.data.data) {
          this.detailRecord = {
            ...record,
            ...response.data.data
          };
        }
      } catch (error) {
        // 列表数据已足够展示基础详情，详情接口失败时不打断用户。
      }
    },
    openEdit(record) {
      this.editingRecord = record;
      this.editForm = this.createEditForm(record);
      this.showEditPopup = true;
    },
    createEditForm(record) {
      const values = clone(record);
      this.fieldsForRecord(record).forEach(field => {
        if (typeof values[field.key] === 'undefined') {
          values[field.key] = '';
        }
      });
      return values;
    },
    openRemark(record) {
      this.remarkForm = {
        id: record.id,
        remark: record.remark || record.adminRemark || ''
      };
      this.showRemarkPopup = true;
    },
    async submitRemark() {
      if (!this.remarkForm.id) {
        return;
      }
      this.savingRemark = true;
      try {
        const response = await axios.post('/api/AccessRegistration/UpdateRemark', {
          Id: this.remarkForm.id,
          Remark: this.remarkForm.remark
        });
        if (!response || !response.data || !response.data.success) {
          this.$toast.fail((response && response.data && response.data.message) || '保存备注失败');
          return;
        }
        this.patchLocalRecord(this.remarkForm.id, { remark: this.remarkForm.remark, adminRemark: this.remarkForm.remark });
        this.showRemarkPopup = false;
        this.$toast.success('备注已保存');
      } catch (error) {
        this.$toast.fail('保存备注失败，请检查网络');
      } finally {
        this.savingRemark = false;
      }
    },
    confirmVoid(record) {
      this.$dialog.confirm({
        title: '确认删除',
        message: `确定软删除“${record.name || record.plateNumber || record.recordNo || '该记录'}”吗？`
      }).then(() => {
        this.voidRecord(record);
      }).catch(() => {});
    },
    async voidRecord(record) {
      try {
        const response = await axios.post('/api/AccessRegistration/Void', { Id: record.id });
        if (!response || !response.data || !response.data.success) {
          this.$toast.fail((response && response.data && response.data.message) || '删除失败');
          return;
        }
        this.records = this.records.filter(item => item.id !== record.id);
        this.totalCount = Math.max(0, this.totalCount - 1);
        this.$toast.success('已删除');
      } catch (error) {
        this.$toast.fail('删除失败，请检查网络');
      }
    },
    async submitEdit() {
      if (!this.editingRecord || !this.editingRecord.id) {
        return;
      }

      this.savingEdit = true;
      try {
        const payload = {
          ...this.editForm,
          Id: this.editingRecord.id,
          ModuleType: this.editingRecord.moduleType,
          FormDataJson: JSON.stringify(this.editForm)
        };
        const response = await axios.post('/api/AccessRegistration/Update', payload);
        if (!response || !response.data || !response.data.success) {
          this.$toast.fail((response && response.data && response.data.message) || '保存修改失败');
          return;
        }
        const saved = response.data.data || this.editForm;
        this.patchLocalRecord(this.editingRecord.id, saved);
        this.showEditPopup = false;
        this.$toast.success('修改已保存');
      } catch (error) {
        this.$toast.fail('保存修改失败，请检查网络');
      } finally {
        this.savingEdit = false;
      }
    },
    patchLocalRecord(id, values) {
      this.records = this.records.map(item => item.id === id ? { ...item, ...values } : item);
      if (this.detailRecord && this.detailRecord.id === id) {
        this.detailRecord = { ...this.detailRecord, ...values };
      }
    },
    fieldsForRecord(record) {
      const config = getAccessRegistrationConfig(record && record.moduleType);
      return config && config.fields ? config.fields : [];
    },
    isEditableField(field) {
      return !['image'].includes(field.type);
    },
    moduleTitle(moduleType) {
      const config = getAccessRegistrationConfig(moduleType);
      return config ? config.title : (moduleType || '-');
    },
    moduleConfig(moduleType) {
      return getAccessRegistrationConfig(moduleType) || {};
    },
    getOptions(field) {
      const moduleType = this.editingRecord ? this.editingRecord.moduleType : this.filters.moduleType;
      return this.optionsMap[`${moduleType}:${field.optionGroup}`] || field.defaultOptions || [];
    },
    getOptionText(field, value, moduleType = '') {
      const ownerModuleType = moduleType || (this.editingRecord && this.editingRecord.moduleType) || (this.detailRecord && this.detailRecord.moduleType) || '';
      const options = this.optionsMap[`${ownerModuleType}:${field.optionGroup}`] || field.defaultOptions || [];
      const item = options.find(option => String(option.code) === String(value));
      return item ? item.description : (value || '-');
    },
    displayFieldValue(field, record) {
      const value = record[field.key];
      if (field.type === 'select') {
        return this.getOptionText(field, value, record.moduleType);
      }
      if (field.type === 'datetime') {
        return this.formatTime(value) || '-';
      }
      return value || '-';
    },
    openEditSelect(field) {
      this.activeEditSelectField = field;
      this.showEditSelectPopup = true;
    },
    selectEditOption(code) {
      if (!this.activeEditSelectField) {
        return;
      }
      this.$set(this.editForm, this.activeEditSelectField.key, code);
      this.showEditSelectPopup = false;
    },
    openEditDateTime(field) {
      this.activeEditDateTimeField = field;
      const current = new Date(this.editForm[field.key]);
      this.editDateTimeValue = Number.isNaN(current.getTime()) ? new Date() : current;
      this.showEditDateTimePopup = true;
    },
    confirmEditDateTime() {
      if (this.activeEditDateTimeField) {
        this.$set(this.editForm, this.activeEditDateTimeField.key, this.editDateTimeValue.toISOString());
      }
      this.showEditDateTimePopup = false;
    },
    formatDateTimeColumn(type, value) {
      const units = { year: '年', month: '月', day: '日', hour: '时', minute: '分' };
      return units[type] ? `${value}${units[type]}` : value;
    },
    formatTime(value) {
      if (!value) {
        return '-';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const yy = date.getFullYear();
      const mm = `${date.getMonth() + 1}`.padStart(2, '0');
      const dd = `${date.getDate()}`.padStart(2, '0');
      const hh = `${date.getHours()}`.padStart(2, '0');
      const mi = `${date.getMinutes()}`.padStart(2, '0');
      return `${yy}-${mm}-${dd} ${hh}:${mi}`;
    }
  },
  async mounted() {
    await this.bootstrapDictionaries();
    this.fetchRecords({ reset: true });
  }
};
</script>

<style scoped>
.access-records-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
}

.filter-card,
.summary-card,
.record-card {
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(215, 229, 251, 0.96);
  box-shadow: var(--shadow-card);
}

.filter-card {
  padding: 10px;
}

.module-filter-row {
  margin-top: 8px;
}

.filter-actions,
.popup-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.summary-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 14px;
}

.summary-item {
  padding: 12px;
  border-radius: 18px;
  background: #f6f9ff;
}

.summary-item span {
  display: block;
  color: var(--text-secondary);
  font-size: 12px;
}

.summary-item strong {
  display: block;
  margin-top: 6px;
  color: var(--text-primary);
  font-size: 24px;
}

.record-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}

.record-card {
  overflow: hidden;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.record-title {
  color: var(--text-primary);
  font-size: 17px;
  font-weight: 800;
}

.record-subtitle,
.record-time {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.6;
}

.record-time {
  text-align: right;
  flex-shrink: 0;
}

.record-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  padding: 12px 0 6px;
}

.record-meta-item {
  min-width: 0;
  padding: 8px 0;
  border-bottom: 1px solid #edf3fb;
}

.record-meta-item span {
  display: block;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 1.3;
}

.record-meta-item strong {
  display: block;
  margin-top: 4px;
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
}

.record-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.picker-popup,
.detail-popup,
.edit-popup,
.remark-popup,
.datetime-popup {
  height: 100%;
  padding: 18px;
  box-sizing: border-box;
  overflow-y: auto;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.popup-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
}

.picker-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 4px;
  border-bottom: 1px solid #eef3f9;
}

.detail-list,
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f6f9ff;
  border: 1px solid #e1ebfb;
}

.detail-row--block {
  flex-direction: column;
}

.detail-row span {
  color: var(--text-secondary);
  font-size: 14px;
}

.detail-row strong {
  color: var(--text-primary);
  font-size: 14px;
  text-align: right;
  word-break: break-all;
}

.form-block {
  padding-bottom: 8px;
}

.field-label {
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 767px) {
  .access-records-page {
    gap: 14px;
  }

  .record-list {
    grid-template-columns: 1fr;
  }

  .record-meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
  }

  .detail-row {
    flex-direction: column;
    gap: 6px;
  }

  .detail-row strong {
    text-align: left;
  }
}
</style>
